import * as React from 'react';
import { useDispatch } from 'react-redux';
import { checkedFetchAccessTokenIfNeeded } from '~/AccessToken';
import { createDownloadUrl } from '~/Api/Download';
import { invokeApi } from '~/ApiClient';
import { ThunkDispatch } from '~/Store';
import { Link } from '~/UI/Button';
import Heading from '~/UI/Heading';

import * as styles from './styles.less';

const useDownload = (): [(accessToken: string, fileId: string, fileName: string) => void, string | null] => {
	const dispatch = useDispatch<ThunkDispatch<never>>();
	const iframeRef = React.useRef<HTMLIFrameElement | null>(null);
	const [link, setLink] = React.useState<string | null>(null);

	const detach = () => {
		if (iframeRef.current !== null) {
			iframeRef.current.parentElement?.removeChild(iframeRef.current);
			iframeRef.current = null;
		}
	};

	const download = (accessToken: string, fileId: string, fileName: string) => {
		setLink(
			dispatch(invokeApi((apiClient) => `${apiClient.baseUrl}${createDownloadUrl(fileId, fileName, accessToken)}`))
		);
		detach();

		const iframe = document.createElement('iframe');
		iframe.style.display = 'none';
		document.body.appendChild(iframe);

		const { contentDocument } = iframe;
		if (contentDocument === null) {
			return;
		}

		const form = contentDocument.createElement('form');
		form.method = 'post';
		form.action = dispatch(invokeApi((apiClient) => `${apiClient.baseUrl}${createDownloadUrl(fileId, fileName, '')}`));
		contentDocument.body.appendChild(form);

		const accessTokenInput = contentDocument.createElement('input');
		accessTokenInput.type = 'hidden';
		accessTokenInput.name = '_accessToken';
		accessTokenInput.value = accessToken;
		form.appendChild(accessTokenInput);

		iframeRef.current = iframe;
		form.submit();
	};

	React.useEffect(() => detach, []);

	return [download, link];
};

export const Download: React.FunctionComponent<
	React.PropsWithChildren<{
		fileId: string;
		fileName: string;
	}>
> = ({ fileId, fileName }) => {
	const dispatch = useDispatch<ThunkDispatch<never>>();
	const [download, link] = useDownload();

	React.useEffect(() => {
		(async () => {
			const accessTokenResponse = await dispatch(checkedFetchAccessTokenIfNeeded());
			if (accessTokenResponse !== null) {
				download(accessTokenResponse.accessToken.token, fileId, fileName);
			}
		})();
	}, []);

	return (
		<section className={styles.section}>
			<div className={styles.in}>
				<Heading>Downloading…</Heading>
				{link !== null ? (
					<Heading level={3}>
						Your download will begin shortly. If it does not, please use <Link href={link}>direct link.</Link>
					</Heading>
				) : null}
			</div>
		</section>
	);
};
